/* eslint-disable */
import { makeStyles } from '@material-ui/core';
import { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import PropTypes from 'prop-types';
import { compare } from 'fast-json-patch';
import { get } from 'lodash';
import RoundedButton from 'components/Shared/RoundedButton';
import Modal from 'components/Shared/Modal';
import { formatPhoneForDisplay, formatPhoneForServer} from 'helpers/phone';
import showServerError from 'helpers/showError';
import PatientForm from '../widgets/PatientForm';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import getFullName from 'helpers/user';
import { useDispatch, useSelector } from 'react-redux';
import { getUtcDate, getLocalizedDate } from '../../../../helpers/localize';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  getTransitionWithVariable,
  setActivePatientInfo,
} from '../../../../redux_store';
import Loader from '../../../Shared/Loader';

const useStyles = makeStyles((theme) => ({
  modal: {
    width: '100%',
    maxWidth: 1000,
    height: '90vh',
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
  },
  loaderStyle: {
    minHeight: 500,
    width: '100%',
    minWidth: 'calc(100vw - 64px)',
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(4, 3),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  actionsContainer: {
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    width: '100%',
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
  },
}));

const PatientEditModal = ({ open, handleClose, accountUuid, patientUuid }) => {
  const classes = useStyles();
  const { en: { errors, notifications, titles, btn } } = useSelector((state) => state.i18n);
  const [backendErrors, setBackendErrors] = useState({});
  const [patient, setPatient] = useState();
  const closeModal = (hasThePatientBeenUpdated) => {
    handleClose(hasThePatientBeenUpdated);
    setBackendErrors({});
    setPatient(null);
  };
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${accountUuid}/patients/${patientUuid}`,
    },
    false,
  ]);

  useEffect(() => {
    if (open && query) {
      query().then(({ error, payload }) => {
        if (!error) {
          setPatient({
            ...payload,
            dateOfBirth: getLocalizedDate(payload.dateOfBirth, 'UTC'),
          });
        }
      });
    }
  }, [query, open]);

  const patientEditAction = (patches) => ({
    method: 'PATCH',
    endpoint: `/accounts/${accountUuid}/patients/${patientUuid}`,
    body: patches,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [
    patientEditAction,
  ]);

  const editPatient = async (values, { setSubmitting }) => {
    const patches = compare(
      {
        ...patient,
      },
      {
        ...values,
        dateOfBirth: values.dateOfBirth.includes('Z')
          ? values.dateOfBirth
          : getUtcDate(values.dateOfBirth),
        phone: {
          ...values.phone,
          phone: formatPhoneForServer(values?.phone?.phone || ''),
        },
      },
    );

    if (patches.length) {
      setBackendErrors({});
      const { payload, error, status } = await mutate(patches);
      if (error) {
        setIsFailed(true);
        setTimeout(() => {
          setIsFailed(false);
        }, 2000);
        const options = payload && {
          correlationUUID: payload.correlationUUID,
          userUUID: user.uuid,
        };
        switch (status) {
          case 422: {
            showServerError(dispatch, errors.validationError);
            setBackendErrors(payload.validationErrors);
            break;
          }
          default: {
            showServerError(dispatch, '', options);
            break;
          }
        }
      } else if (payload) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
        }, 2000);
        setPatient(payload);
        dispatch(setActivePatientInfo({
          ...payload,
          dateOfBirth: getLocalizedDate(payload.dateOfBirth, 'UTC'),
          phone: {
            ...payload.phone,
            phone: formatPhoneForDisplay(get(payload, 'phone.phone', '')),
          },
        }));
        closeModal(true);
        dispatch(
          enqueueSnackbar({
            message: getTransitionWithVariable(notifications.patientEdited, {
              name: getFullName(values),
            }),
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
          }),
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      classes={{ paper: classes.modal }}
      title={titles.editPatient}
      handleClose={() => closeModal(false)}
    >
      {
        !patient && <Loader
          fullHeight
          className={classes.loaderStyle}
        />
      }
      {
        patient && <Formik
          initialValues={patient}
          validateOnBlur={false}
          enableReinitialize
          onSubmit={editPatient}
        >
          {({ isValid, isSubmitting, dirty, handleSubmit, values }) => (
            <>
              <div className={classes.scrollableContent}>
                <PatientForm
                  values={values}
                  backendErrors={backendErrors}
                />
              </div>
              <div className={classes.actionsContainer}>
                <RoundedButton
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={() => closeModal(false)}
                  className={classes.actionButton}
                >
                  {btn.cancel}
                </RoundedButton>
                <RoundedButton
                  variant="contained"
                  color="primary"
                  size="medium"
                  disabled={!isValid || isSubmitting || !dirty}
                  onClick={handleSubmit}
                  isLoading={loading}
                  isSuccess={isSuccess}
                  isFailed={isFailed}
                  className={classes.actionButton}
                >
                  {btn.save}
                </RoundedButton>
              </div>
            </>
          )}
        </Formik>
      }
    </Modal>
  );
};

PatientEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  patientUuid: PropTypes.string.isRequired,
  accountUuid: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default PatientEditModal;
